/**
 * Authentication service for Firebase Auth with Google and Facebook OAuth
 */

import {
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification as firebaseSendEmailVerification,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  linkWithPopup,
  updateProfile,
} from 'firebase/auth';
import { getAuthInstance } from './firebase';
import { API_CONFIG } from '../config/api.js';
import { t } from '../i18n/config.js';

// Re-export fetchSignInMethodsForEmail for use in UI components
export { fetchSignInMethodsForEmail };

/**
 * Check if user is suspended
 * @param {Object} user - Firebase user object
 * @returns {Promise<boolean>} True if user is suspended
 */
async function checkUserSuspension(user) {
  try {
    const token = await user.getIdToken();
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.uid}/suspension-status`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.suspended === true;
    }

    // If endpoint fails, allow sign-in (fail open for better UX)
    console.warn('Failed to check suspension status, allowing sign-in');
    return false;
  } catch (error) {
    console.error('Error checking suspension status:', error);
    // Fail open - allow sign-in if check fails
    return false;
  }
}

/**
 * Check for pending credential and link it to current user
 * @param {Object} user - Firebase user object
 */
async function checkAndLinkPendingCredential(user) {
  try {
    const pendingCredentialStr = sessionStorage.getItem('pendingCredential');
    const pendingEmail = sessionStorage.getItem('pendingCredentialEmail');

    if (!pendingCredentialStr || !pendingEmail) {
      return; // No pending credential
    }

    // Check if the email matches
    if (user.email !== pendingEmail) {
      console.log('Pending credential email does not match current user');
      sessionStorage.removeItem('pendingCredential');
      sessionStorage.removeItem('pendingCredentialEmail');
      return;
    }

    const pendingCredData = JSON.parse(pendingCredentialStr);
    console.log('Found pending credential to link:', pendingCredData.providerId);

    // Recreate the credential
    let credential;
    if (pendingCredData.providerId === 'facebook.com') {
      credential = FacebookAuthProvider.credential(pendingCredData.accessToken);
    } else if (pendingCredData.providerId === 'google.com') {
      credential = GoogleAuthProvider.credential(
        pendingCredData.idToken,
        pendingCredData.accessToken
      );
    }

    if (credential) {
      // Link the credential
      await linkWithCredential(user, credential);
      console.log('Successfully linked pending credential:', pendingCredData.providerId);

      // Clear the pending credential
      sessionStorage.removeItem('pendingCredential');
      sessionStorage.removeItem('pendingCredentialEmail');

      // Show success message
      alert(`${pendingCredData.providerId === 'facebook.com' ? 'Facebook' : 'Google'} is nou gekoppel aan jou rekening!`);
    }
  } catch (error) {
    console.error('Failed to link pending credential:', error);
    // Clear the pending credential even on error
    sessionStorage.removeItem('pendingCredential');
    sessionStorage.removeItem('pendingCredentialEmail');

    // Don't throw - linking failure shouldn't prevent sign-in
    if (error.code !== 'auth/provider-already-linked') {
      console.warn('Could not link pending credential, but sign-in succeeded');
    }
  }
}

/**
 * Check if email is on allowlist
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} True if email is allowed
 */
async function checkEmailAllowlist(email) {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/allowlist/check?email=${encodeURIComponent(email)}`);

    if (response.ok) {
      const data = await response.json();
      return data.allowed === true;
    }

    // If endpoint fails, allow registration (fail open for better UX)
    console.warn('Failed to check allowlist, allowing registration');
    return true;
  } catch (error) {
    console.error('Error checking allowlist:', error);
    // Fail open - allow registration if check fails
    return true;
  }
}

/**
 * Sign in with Google
 */
export async function signInWithGoogle() {
  try {
    const auth = getAuthInstance();
    const provider = new GoogleAuthProvider();

    // Request email and profile scopes
    provider.addScope('profile');
    provider.addScope('email');

    const result = await signInWithPopup(auth, provider);

    // Get user info
    const user = result.user;
    const credential = GoogleAuthProvider.credentialFromResult(result);

    console.log('Google sign-in successful:', user.email);

    // Check if user is suspended
    const isSuspended = await checkUserSuspension(user);
    if (isSuspended) {
      // Sign out immediately
      await firebaseSignOut(auth);
      throw {
        code: 'auth/user-suspended',
        message: 'Your account has been suspended. Please contact the administrators at support@kassie.media',
      };
    }

    // Check for pending credential to link
    await checkAndLinkPendingCredential(user);

    return {
      user,
      credential,
      providerId: 'google.com',
    };
  } catch (error) {
    console.error('Google sign-in error:', error);

    // Handle account linking
    if (error.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData?.email;
      const credential = error.credential;

      if (email && credential) {
        try {
          // Store the pending credential for later linking
          sessionStorage.setItem('pendingCredential', JSON.stringify({
            providerId: credential.providerId,
            idToken: credential.idToken,
            accessToken: credential.accessToken,
          }));
          sessionStorage.setItem('pendingCredentialEmail', email);

          // Check which provider the user should use
          const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/check-email/${encodeURIComponent(email)}`);
          if (response.ok) {
            const data = await response.json();
            if (data.exists && data.provider) {
              const providerName = data.provider === 'facebook.com' ? 'Facebook' :
                                   data.provider === 'password' ? t('auth.emailPassword', 'E-pos/Wagwoord') :
                                   data.provider;
              throw {
                code: 'auth/account-exists-different-provider',
                message: t('auth.accountExistsSignInFirst', `'n Rekening met hierdie e-pos bestaan reeds. Meld asseblief eers aan met ${providerName}. Jy kan dan Google koppel vanaf Rekening Instellings.`),
                email,
                originalProvider: data.provider,
              };
            }
          }
        } catch (checkError) {
          console.error('Failed to check email provider:', checkError);
          // If check fails, show generic message
        }
      }

      // Fallback message if we couldn't determine the provider
      throw {
        code: 'auth/account-exists-different-provider',
        message: t('auth.accountExistsLinkFromSettings', "'n Rekening met hierdie e-pos bestaan reeds. Meld asseblief eers aan met jou oorspronklike verskaffer. Jy kan dan ander verskaffers koppel vanaf Rekening Instellings."),
        email: error.customData?.email,
      };
    }

    throw handleAuthError(error);
  }
}

/**
 * Sign in with Facebook
 */
export async function signInWithFacebook() {
  try {
    const auth = getAuthInstance();
    const provider = new FacebookAuthProvider();

    // Request email scope
    provider.addScope('email');
    provider.addScope('public_profile');

    const result = await signInWithPopup(auth, provider);

    // Get user info
    const user = result.user;
    const credential = FacebookAuthProvider.credentialFromResult(result);

    console.log('Facebook sign-in successful:', user.email);

    // Check if user is suspended
    const isSuspended = await checkUserSuspension(user);
    if (isSuspended) {
      // Sign out immediately
      await firebaseSignOut(auth);
      throw {
        code: 'auth/user-suspended',
        message: 'Your account has been suspended. Please contact the administrators at support@kassie.media',
      };
    }

    // Check for pending credential to link
    await checkAndLinkPendingCredential(user);

    return {
      user,
      credential,
      providerId: 'facebook.com',
    };
  } catch (error) {
    console.error('Facebook sign-in error:', error);
    console.log('Error details:', {
      code: error.code,
      email: error.customData?.email,
      credential: error.credential,
      customData: error.customData
    });

    // Handle account linking
    if (error.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData?.email;
      const credential = error.credential;

      if (email && credential) {
        try {
          // Store the pending credential for later linking
          sessionStorage.setItem('pendingCredential', JSON.stringify({
            providerId: credential.providerId,
            accessToken: credential.accessToken,
            secret: credential.secret,
          }));
          sessionStorage.setItem('pendingCredentialEmail', email);

          // Check which provider the user should use
          const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/check-email/${encodeURIComponent(email)}`);
          if (response.ok) {
            const data = await response.json();
            if (data.exists && data.provider) {
              const providerName = data.provider === 'google.com' ? 'Google' :
                                   data.provider === 'password' ? t('auth.emailPassword', 'E-pos/Wagwoord') :
                                   data.provider;
              throw {
                code: 'auth/account-exists-different-provider',
                message: t('auth.accountExistsSignInFirst', `'n Rekening met hierdie e-pos bestaan reeds. Meld asseblief eers aan met ${providerName}. Jy kan dan Facebook koppel vanaf Rekening Instellings.`),
                email,
                originalProvider: data.provider,
              };
            }
          }
        } catch (checkError) {
          console.error('Failed to check email provider:', checkError);
          // If check fails, show generic message
        }
      }

      // Fallback message if we couldn't determine the provider
      throw {
        code: 'auth/account-exists-different-provider',
        message: t('auth.accountExistsLinkFromSettings', "'n Rekening met hierdie e-pos bestaan reeds. Meld asseblief eers aan met jou oorspronklike verskaffer. Jy kan dan ander verskaffers koppel vanaf Rekening Instellings."),
        email: error.customData?.email,
      };
    }

    throw handleAuthError(error);
  }
}

/**
 * Sign out current user
 */
export async function signOut() {
  try {
    const auth = getAuthInstance();
    await firebaseSignOut(auth);
    console.log('User signed out successfully');
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

/**
 * Get current user
 */
export function getCurrentUser() {
  const auth = getAuthInstance();
  return auth.currentUser;
}

/**
 * Listen to authentication state changes
 * @param {Function} callback - Called when auth state changes with user object or null
 * @returns {Function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  const auth = getAuthInstance();
  return onAuthStateChanged(auth, (user) => {
    if (user) {
      console.log('User is signed in:', user.email);
      callback(user);
    } else {
      console.log('User is signed out');
      callback(null);
    }
  });
}

/**
 * Handle authentication errors and return error codes for translation
 */
function handleAuthError(error) {
  const errorCode = error.code;

  // Return error code for translation in UI layer
  return {
    code: errorCode,
    message: error.message,
    originalError: error,
  };
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return getCurrentUser() !== null;
}

/**
 * Validate password meets requirements
 * @param {string} password - Password to validate
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validatePassword(password) {
  const errors = [];

  if (!password || password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export function validateEmail(email) {
  if (!email) return false;

  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Sign up with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {Object} options - Additional options
 * @param {boolean} options.bypassAllowlist - If true, skip allowlist check (for family invitations)
 * @returns {Promise<{user, needsVerification}>}
 */
export async function signUpWithEmail(email, password, options = {}) {
  try {
    const auth = getAuthInstance();

    // Validate email
    if (!validateEmail(email)) {
      throw {
        code: 'auth/invalid-email',
        message: 'Please enter a valid email address',
      };
    }

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      throw {
        code: 'auth/weak-password',
        message: passwordValidation.errors.join('. '),
      };
    }

    // Check allowlist unless bypassed (for family invitations)
    if (!options.bypassAllowlist) {
      const isAllowed = await checkEmailAllowlist(email);
      if (!isAllowed) {
        throw {
          code: 'auth/email-not-allowed',
          message: 'Registration is currently limited to invited users. Please contact support@kassie.media for access.',
        };
      }
    } else {
      console.log('Bypassing allowlist check for family invitation');
    }

    // Create user account
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;

    console.log('Email/password sign-up successful:', user.email);

    // Set display name if provided
    if (options.displayName) {
      try {
        await updateProfile(user, {
          displayName: options.displayName
        });
        console.log('Display name set to:', options.displayName);
      } catch (profileError) {
        console.error('Failed to set display name:', profileError);
        // Don't fail the sign-up if profile update fails
      }
    }

    // Send email verification
    try {
      await firebaseSendEmailVerification(user);
      console.log('Verification email sent to:', user.email);
    } catch (verificationError) {
      console.error('Failed to send verification email:', verificationError);
      // Don't fail the sign-up if verification email fails
    }

    return {
      user,
      needsVerification: !user.emailVerified,
      providerId: 'password',
    };
  } catch (error) {
    console.error('Email/password sign-up error:', error);
    throw handleAuthError(error);
  }
}

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{user}>}
 */
export async function signInWithEmail(email, password) {
  try {
    const auth = getAuthInstance();

    // Validate email
    if (!validateEmail(email)) {
      throw {
        code: 'auth/invalid-email',
        message: 'Please enter a valid email address',
      };
    }

    // Sign in
    const result = await signInWithEmailAndPassword(auth, email, password);
    const user = result.user;

    console.log('Email/password sign-in successful:', user.email);

    // Check if user is suspended
    const isSuspended = await checkUserSuspension(user);
    if (isSuspended) {
      // Sign out immediately
      await firebaseSignOut(auth);
      throw {
        code: 'auth/user-suspended',
        message: 'Your account has been suspended. Please contact the administrators at support@kassie.media',
      };
    }

    return {
      user,
      needsVerification: !user.emailVerified,
      providerId: 'password',
    };
  } catch (error) {
    console.error('Email/password sign-in error:', error);
    throw handleAuthError(error);
  }
}

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise<void>}
 */
export async function sendPasswordReset(email) {
  try {
    const auth = getAuthInstance();

    // Validate email
    if (!validateEmail(email)) {
      throw {
        code: 'auth/invalid-email',
        message: 'Please enter a valid email address',
      };
    }

    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent to:', email);
  } catch (error) {
    // Don't reveal if email exists - always show success for security
    if (error.code === 'auth/user-not-found') {
      console.log('Password reset requested for non-existent email:', email);
      return; // Silently succeed
    }

    console.error('Password reset error:', error);
    throw handleAuthError(error);
  }
}

/**
 * Send email verification to current user
 * @returns {Promise<void>}
 */
export async function sendEmailVerification() {
  try {
    const auth = getAuthInstance();
    const user = auth.currentUser;

    if (!user) {
      throw {
        code: 'auth/no-current-user',
        message: 'No user is currently signed in',
      };
    }

    if (user.emailVerified) {
      console.log('Email already verified for:', user.email);
      return;
    }

    await firebaseSendEmailVerification(user);
    console.log('Verification email sent to:', user.email);
  } catch (error) {
    console.error('Send verification email error:', error);
    throw handleAuthError(error);
  }
}

/**
 * Check if current user's email is verified
 * @returns {boolean}
 */
export function isEmailVerified() {
  const auth = getAuthInstance();
  const user = auth.currentUser;
  return user ? user.emailVerified : false;
}

/**
 * Handle account linking when auth/account-exists-with-different-credential error occurs
 * @param {Error} error - Firebase auth error
 * @param {string} email - User email from error
 * @returns {Promise<{user, linked: boolean, methods: string[]}>}
 */
// eslint-disable-next-line no-unused-vars
async function handleAccountLinking(error, email) {
  try {
    console.log('handleAccountLinking called with email:', email);
    const auth = getAuthInstance();

    // Get the pending credential from the error
    const pendingCred = error.credential;
    console.log('Pending credential:', pendingCred);

    // Fetch sign-in methods for the email
    // Note: This may return empty array for privacy reasons (email enumeration protection)
    console.log('Fetching sign-in methods for:', email);
    const methods = await fetchSignInMethodsForEmail(auth, email);
    console.log('Sign-in methods found:', methods);

    // Firebase returns empty array for privacy, but we know account exists from the error
    // Just tell user to use their existing sign-in method
    const errorMessage = {
      code: 'auth/account-exists-different-method',
      message: `An account with ${email} already exists. Please sign in using the method you originally used (Google, email/password, or Facebook).`,
    };

    throw errorMessage;
  } catch (linkError) {
    console.error('Account linking failed:', linkError);
    throw {
      code: 'auth/linking-failed',
      message: 'Failed to link accounts. Please try again.',
    };
  }
}

/**
 * Link a credential to the current user
 * @param {Object} credential - The credential to link
 * @returns {Promise<{user}>}
 */
export async function linkCredentialToCurrentUser(credential) {
  try {
    const auth = getAuthInstance();
    const user = auth.currentUser;

    if (!user) {
      throw {
        code: 'auth/no-current-user',
        message: 'No user is currently signed in',
      };
    }

    const result = await linkWithCredential(user, credential);
    console.log('Credential linked successfully:', result.user.email);

    return {
      user: result.user,
      linked: true,
    };
  } catch (error) {
    console.error('Link credential error:', error);
    throw handleAuthError(error);
  }
}


/**
 * Link OAuth provider to existing account (Task 9.1)
 * @param {string} email - User email
 * @param {Object} credential - OAuth credential from Firebase
 * @returns {Promise<{success: boolean, primaryUid: string}>}
 */
export async function linkOAuthProvider(email, credential) {
  try {
    const user = getCurrentUser();
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/link-provider`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify({
        email,
        providerId: credential.providerId,
        accessToken: credential.accessToken,
        idToken: credential.idToken,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Link OAuth provider error:', error);
    throw error;
  }
}

/**
 * Add password to OAuth account (Task 9.2)
 * @param {string} password - New password
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function addPassword(password) {
  try {
    // Validate password on client side
    const validation = validatePassword(password);
    if (!validation.valid) {
      throw {
        code: 'auth/weak-password',
        message: validation.errors.join('. '),
      };
    }

    const user = getCurrentUser();
    if (!user) {
      throw {
        code: 'auth/no-current-user',
        message: 'No user is currently signed in',
      };
    }

    const token = await user.getIdToken();

    const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/me/add-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Add password error:', error);
    throw error;
  }
}

/**
 * Change password for email/password account (Task 9.3)
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function changePassword(currentPassword, newPassword) {
  try {
    // Validate new password on client side
    const validation = validatePassword(newPassword);
    if (!validation.valid) {
      throw {
        code: 'auth/weak-password',
        message: validation.errors.join('. '),
      };
    }

    const user = getCurrentUser();
    if (!user) {
      throw {
        code: 'auth/no-current-user',
        message: 'No user is currently signed in',
      };
    }

    const token = await user.getIdToken();

    const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/me/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Change password error:', error);
    throw error;
  }
}

/**
 * Link a provider to the current user's account using popup
 * @param {string} providerId - Provider ID ('google.com' or 'facebook.com')
 * @returns {Promise<{user}>}
 */
export async function linkProviderWithPopup(providerId) {
  try {
    const auth = getAuthInstance();
    const user = auth.currentUser;

    if (!user) {
      throw {
        code: 'auth/no-current-user',
        message: t('auth.error.noUser', 'No user is currently signed in'),
      };
    }

    let provider;
    if (providerId === 'google.com') {
      provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
    } else if (providerId === 'facebook.com') {
      provider = new FacebookAuthProvider();
      provider.addScope('email');
      provider.addScope('public_profile');
    } else {
      throw {
        code: 'auth/invalid-provider',
        message: t('auth.error.invalidProvider', 'Invalid provider'),
      };
    }

    const result = await linkWithPopup(user, provider);
    console.log(`${providerId} linked successfully:`, result.user.email);

    return {
      user: result.user,
      linked: true,
    };
  } catch (error) {
    console.error(`Link ${providerId} error:`, error);
    throw handleAuthError(error);
  }
}


/**
 * Get connected authentication providers (Task 9.4)
 * @returns {Promise<string[]>} List of provider IDs
 */
export async function getConnectedProviders() {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw {
        code: 'auth/no-current-user',
        message: 'No user is currently signed in',
      };
    }

    const token = await user.getIdToken();

    const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/me/providers`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }

    const result = await response.json();
    return result.providers || [];
  } catch (error) {
    console.error('Get connected providers error:', error);
    throw error;
  }
}

/**
 * Update the current user's Firebase Auth profile (displayName, photoURL)
 * @param {Object} profileData - Object with optional displayName and/or photoURL
 * @returns {Promise<void>}
 */
export async function updateUserProfile(profileData) {
  const user = getCurrentUser();
  if (!user) {
    throw { code: 'auth/no-current-user', message: 'No user is currently signed in' };
  }
  await updateProfile(user, profileData);
}
